import React, { useMemo } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import type { ClientDashboardSetting } from "../api/clientManagement";
import { DASHBOARD_SETTINGS } from "../../../../shared/utils";
import { SettingsSection } from "./SettingsSection";
import { SectionCard } from "./SectionCard";

interface DashboardAccessTabProps {
  effectiveClientId: string | null;
  dashboardSettingsQuery: UseQueryResult<{ data: ClientDashboardSetting[] }>;
  updateDashboardSettingMutation: UseMutationResult<
    unknown,
    unknown,
    { dashboardKey: string; payload: { is_enabled?: boolean; ai_enabled?: boolean | null } }
  >;
}

export const DashboardAccessTab: React.FC<DashboardAccessTabProps> = ({
  effectiveClientId,
  dashboardSettingsQuery,
  updateDashboardSettingMutation,
}) => {
  const settingsMap = useMemo(() => {
    const map = new Map<string, ClientDashboardSetting>();
    dashboardSettingsQuery.data?.data?.forEach((setting) => {
      map.set(setting.dashboard_key, setting);
    });
    return map;
  }, [dashboardSettingsQuery.data]);

  return (
    <SettingsSection
      title="Dashboard Access"
      description="Enable or disable dashboards per client and control AI features."
    >
      {!effectiveClientId && (
        <Alert severity="info">Select a client to manage dashboard access.</Alert>
      )}
      <SectionCard title="Menu configuration" description="Dashboards toggled off will be hidden and inaccessible.">
        {dashboardSettingsQuery.isLoading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2">Loading dashboard settings...</Typography>
          </Stack>
        ) : (
          <TableContainer sx={{ width: "100%" }}>
            <Table size="small" sx={{ width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell>Dashboard</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Enabled</TableCell>
                  <TableCell>AI features</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {DASHBOARD_SETTINGS.map((dashboard) => {
                  const setting = settingsMap.get(dashboard.key);
                  const isEnabled = setting?.is_enabled ?? true;
                  const aiEnabled =
                    setting?.ai_enabled ??
                    (dashboard.aiSupported ? true : null);
                  const disableControls =
                    !effectiveClientId || updateDashboardSettingMutation.isPending;

                  return (
                    <TableRow key={dashboard.key} sx={{ "& td": { py: 1.5 } }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {dashboard.label}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {dashboard.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isEnabled}
                              disabled={disableControls}
                              onChange={(event) =>
                                updateDashboardSettingMutation.mutate({
                                  dashboardKey: dashboard.key,
                                  payload: { is_enabled: event.target.checked },
                                })
                              }
                            />
                          }
                          label={isEnabled ? "Enabled" : "Disabled"}
                        />
                      </TableCell>
                      <TableCell>
                        {dashboard.aiSupported ? (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(aiEnabled)}
                                disabled={disableControls || !isEnabled}
                                onChange={(event) =>
                                  updateDashboardSettingMutation.mutate({
                                    dashboardKey: dashboard.key,
                                    payload: { ai_enabled: event.target.checked },
                                  })
                                }
                              />
                            }
                            label={aiEnabled ? "On" : "Off"}
                          />
                        ) : (
                          <Box sx={{ color: "text.secondary" }}>—</Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </SectionCard>
    </SettingsSection>
  );
};
